import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MockTv } from './MockTv.js';
import './landing.css';

type Game = { slug: string; title: string; tags: string[] };

/** Stable, cheerful cover colours — same rule the server and dashboard use. */
const COVERS = ['#22D3EE', '#34D399', '#FBBF24', '#F472B6', '#818CF8', '#FB7185'];
const coverFor = (slug: string) => {
  let hash = 0;
  for (const ch of slug) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return COVERS[hash % COVERS.length] ?? COVERS[0]!;
};

/**
 * The showroom — PART F4.
 *
 * The page's single job: a barbershop or dealership owner sees what their TV
 * will look like and believes it in ten seconds. So the hero IS the TV, and
 * everything around it is deliberately quiet.
 *
 * This is the only eagerly-loaded route, so it stays dependency-free: no QR
 * library, no animation library, no font request. Motion is CSS.
 */
export default function Landing() {
  const [games, setGames] = useState<Game[]>([]);

  useEffect(() => {
    let cancelled = false;
    // The shelf reads from the catalogue, so it grows on its own as games ship.
    fetch('/api/games')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unavailable'))))
      .then((body: { games: Game[] }) => {
        if (!cancelled) setGames(body.games ?? []);
      })
      .catch(() => {
        // The page still sells without the shelf; never blank it on a hiccup.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="lf-landing">
      <div className="lf-shell">
        <nav className="lf-nav">
          <span className="lf-wordmark">
            Lobby<span>Fun</span>
          </span>
          <Link to="/auth/login" className="lf-cta lf-cta--ghost">
            Log in
          </Link>
        </nav>

        <header className="lf-hero">
          <div className="lf-hero__copy">
            <p className="lf-eyebrow">For waiting rooms</p>
            <h1 className="lf-h1">Turn your waiting-room TV into an arcade.</h1>
            <p className="lf-lede">
              Customers scan a QR, their phone becomes the controller, and your TV becomes the most
              popular thing in the room.
            </p>
            <Link to="/auth/register" className="lf-cta">
              Get started free
            </Link>
            <p className="lf-hero__note">
              Works on the TV you already own. No app for your customers to download.
            </p>
          </div>

          <MockTv />
        </header>
      </div>

      <div className="lf-shell">
        <section className="lf-section">
          <div className="lf-section__head">
            <p className="lf-eyebrow">The games</p>
            <h2 className="lf-h2">Made for people with five minutes to kill.</h2>
            <p className="lf-lede">
              Short rounds, no instructions, and a leaderboard worth coming back for.
            </p>
          </div>

          <div className="lf-shelf">
            {games.map((game) => (
              <article key={game.slug} className="lf-game">
                <div className="lf-game__cover" style={{ background: coverFor(game.slug) }}>
                  🧱
                </div>
                <div className="lf-game__body">
                  <span className="lf-game__name">{game.title}</span>
                  <div className="lf-tags">
                    {game.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="lf-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}

            <div className="lf-game--soon">More games coming</div>
            <div className="lf-game--soon">More games coming</div>
          </div>
        </section>

        <section className="lf-section">
          <div className="lf-section__head">
            <p className="lf-eyebrow">How it works</p>
            <h2 className="lf-h2">Three steps, and none of them are yours.</h2>
          </div>

          {/* Genuinely a sequence, so it is numbered. */}
          <ol className="lf-steps">
            <li className="lf-step">
              <span className="lf-step__name">Scan</span>
              <p className="lf-step__body">
                A customer points their camera at the screen. The controller opens in their browser.
              </p>
            </li>
            <li className="lf-step">
              <span className="lf-step__name">Play</span>
              <p className="lf-step__body">
                Their phone is the controller. The game runs on your TV, where everyone can see it.
              </p>
            </li>
            <li className="lf-step">
              <span className="lf-step__name">Come back to defend the crown</span>
              <p className="lf-step__body">
                Top the board and you are the Lobby Boss — until somebody else takes it.
              </p>
            </li>
          </ol>
        </section>

        <section className="lf-section">
          <div className="lf-value">
            <div className="lf-value__item">
              <span className="lf-value__lead">Happier waits</span>
              <p className="lf-value__sub">The wait stops being the worst part of the visit.</p>
            </div>
            <div className="lf-value__item">
              <span className="lf-value__lead">Your promos on screen</span>
              <p className="lf-value__sub">Your offer sits beside the game, all day.</p>
            </div>
            <div className="lf-value__item">
              <span className="lf-value__lead">See who comes back</span>
              <p className="lf-value__sub">
                Scans, plays, and returning players — in real numbers.
              </p>
            </div>
          </div>

          <p className="lf-modes">
            <strong>You pick the games</strong> — or let each guest pick from their phone.
          </p>
        </section>

        <section className="lf-close">
          <h2 className="lf-h2">Your TV is already on. Give it something to do.</h2>
          <Link to="/auth/register" className="lf-cta">
            Get started free
          </Link>
        </section>

        <footer className="lf-footer">
          <span>© LobbyFun</span>
          <span>
            <a href="mailto:hello@lobbyfun.com">hello@lobbyfun.com</a> ·{' '}
            <Link to="/privacy">Privacy</Link>
          </span>
        </footer>
      </div>
    </div>
  );
}
